<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex flex-row my-5">
      <div class="w-full text-center">
        <div class="font-extrabold mb-3">Your trip</div>
        <el-date-picker
          v-model="date"
          type="date"
          placeholder="Select a date"
          class="w-full"
          id="mainDatepicker"
          format="MM/dd/yyyy"
          value-format="MM/dd/yyyy"
          @change="handleDateChange"
        >
        </el-date-picker>

        <el-select
            class = 'ml-3'
            v-if = "outfitter.favorite === 1"
            v-model="riverSection"
            @change="handleRiverSelect"
            placeholder="Select a river"
        >
          <el-option
              v-for="riverSection in riverSections"
              :key="riverSection.id"
              :label="getRiverSectionName(riverSection)"
              :value="riverSection.id"
          ></el-option>
        </el-select>

        <div v-if="displayGroupCount" class="font-extrabold my-3">
          Your Guide
        </div>
        <base-input
          v-if="displayGroupCount"
          style="width: 220px; margin: 0 auto"
          v-model="guide"
        ></base-input>
      </div>
    </div>

    <div
      v-for="displayGroup in displayGroups"
      :key="displayGroup.id"
      class="flex flex-row my-5"
    >
      <div class="w-full border border-gray-300 rounded p-3 m-2" :class="{ 'bg-green-50': highlightedGroups.includes(displayGroup.id) }">
        <div class="flex flex-row my-5">
          <div class="font-bold w-6/12 sm:w-10/12">
            {{ displayGroup.name }}
            <span class="ml-2 text-sm text-red-600 font-semibold">
              {{ getRemainingDiscountTime(displayGroup.created_at) }}
            </span>
          </div>
          <div class="w-6/12 sm:w-2/12">
            <button
              @click="handleAddGroup(displayGroup.id)"
              class="group-button inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:border-teal-700 focus:shadow-outline-teal active:bg-green-700 transition ease-in-out duration-150"
            >
              Add Group To Cart
            </button>
          </div>
        </div>

        <div class="flex flex-row">
          <div class="w-full">
            <image-viewer
              :show-footer="false"
              :options="{
                defaultWidth: '200px',
              }"
              :images="
                displayGroup.photos.map((photo) => {
                  return {
                    path: photo.full_path,
                    id: photo.id,
                    s3_path: photo.path,
                    s3_filename: photo.file_name,
                  };
                })
              "
              :displayGroupOnly="outfitter.display_group_only"
              @addToCart="addToCart"
            ></image-viewer>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ImageViewer from "@/components/ui/images/ImageViewer";
import "@/components/ui/images/iconfont";
import BaseInput from "../ui/forms/BaseInput";

export default {
  name: "Search",
  components: { BaseInput, ImageViewer },
  data() {
    return {
      date: null,
      guide: null,
      riverSection: null,
      highlightedGroups: [],
    };
  },
  methods: {
    getRiverSectionName(riverSection) {
      return riverSection.river ? riverSection.river.name + ' - ' + riverSection.name : riverSection.name;
    },
    getRemainingDiscountTime(createdAt) {
      // Don't show discount timer if outfitter doesn't have a 24-hour discount
      if (!this.outfitter?.discount_24) return '';
      
      if (!createdAt) return '';
      
      const createdDate = new Date(createdAt);
      const expiryDate = new Date(createdDate.getTime() + 24 * 60 * 60 * 1000); // 24 hours after creation
      const now = new Date();
      
      // If discount period has expired
      if (now > expiryDate) return '';
      
      // Calculate remaining time
      const remainingMs = expiryDate - now;
      const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
      const remainingMinutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
      
      return `Time remaining for discount: ${remainingHours}h ${remainingMinutes}m`;
    },
    handleRiverSelect() {
      if(this.date) {
        this.triggerSearch();
      }
    },
    loadRiverSections() {
      this.$store.dispatch("riverSection/all", {withDeleted: false});
    },
    handleAddGroup(id) {
      const data = {
        order_id: this.order.id,
        display_group_id: id,
      };
      this.highlightedGroups.push(id);
      this.$notify({
        title: "Cart",
        message: "Adding group to cart",
        type: "success",
        position: "top-left",
      });
      this.$store.dispatch("order/add", data).then((resp) => {
        if (!resp.success) return;

        this.$notify({
          title: "Cart",
          message: "The photo group has been added to your cart",
          type: "success",
          position: "top-left",
        });

        const group = this.displayGroups.find((element) => element.id === id);

        this.$confirm(
          group.name +
            " has been added to your cart. Would you like to proceed to check out or continue shopping?",
          "Checkout",
          {
            distinguishCancelAndClose: true,
            confirmButtonText: "Checkout",
            cancelButtonText: "Continue Shopping",
          }
        ).then(() => {
          this.$router.push({
            name: "cart",
            params: {
              slug: this.$route.params.slug,
              external: this.$route.params.external,
            },
          });
        });
      });
    },
    addToCart(id) {
      const data = {
        order_id: this.order.id,
        photo_id: id,
      };
      this.$notify({
        title: "Cart",
        message: "Adding photo to cart",
        type: "success",
        position: "top-left",
      });
      this.$store.dispatch("order/add", data).then((resp) => {
        if (!resp.success) return;

        this.$notify({
          title: "Cart",
          message: "The photo has been added to your cart",
          type: "success",
          position: "top-left",
        });
      });
    },
    handleDateChange() {
      if(this.outfitter.favorite !== 1) {
        this.triggerSearch();
      }
      if(this.riverSection) {
        this.triggerSearch();
      }
    },

    triggerSearch() {
      this.$notify({
        title: "Search",
        message: "Searching for photos...",
        type: "success",
        position: "top-left",
        duration: 5000,
      });
      this.$store
          .dispatch("search/get_displayGroups", {
            outfitterId: this.outfitter.id,
            date: this.date,
            riverSection: this.riverSection
          })
          .then((resp) => {
            if (!resp.displayGroups || !resp.displayGroups.length) {
              this.$notify({
                title: "Search",
                message: "We don't have any photos for this date",
                type: "warning",
                position: "top-left",
              });
            }
          });
    },

    loadOrder() {
      this.$store
        .dispatch("order/load_order", { slug: this.$route.params.slug })
        .then((resp) => {
          if (resp.order.hash || resp.order.square_transaction) {
            this.$store.dispatch("order/clear_order").then(() => {
              this.loadOrder();
            });
          }
        });
    },
  },
  computed: {
    riverSections: function() {
      let sections = this.$store.getters["riverSection/getRiverSections"];
      console.log(sections);
      return sections;
    },
    outfitter: function () {
      return this.$store.getters["search/getOutfitter"];
    },
    displayGroupCount: function () {
      const groups = this.$store.getters["search/getDisplayGroups"];
      return groups.length;
    },
    displayGroups: function () {
      const groups = this.$store.getters["search/getDisplayGroups"];
      if (this.guide && this.guide.length) {
        return groups.filter((group) => group.name.includes(this.guide));
      }
      return groups;
    },
    order: function () {
      return this.$store.getters["order/getOrder"];
    },
  },
  watch: {
    outfitter: function () {
      if (!this.$route.params.external) {
        this.date = new Date();
        this.handleDateChange();
      }
    },
  },
  mounted() {
    this.loadRiverSections();
    this.loadOrder();
    this.$store.dispatch("search/set_external", this.$route.params.external);
    const datepicker = document.getElementById("mainDatepicker");
    datepicker.setAttribute("readonly", "readonly");
  },
};
</script>
<style>
.el-icon-d-arrow-right {
  display: none !important;
}
.el-icon-d-arrow-left {
  display: none !important;
}
</style>
